/**
 * @file src/offline/offlineDefaultPair.js
 * @description The fallback pair, kept by a download and handed back with no connection.
 *
 * Every account has a default orbiter and a default world, and they are not decoration: they are what
 * ANY fallback resolves to, and they are the whole answer for a file imported from this machine —
 * a local file has no author and no track document, so there is nobody to have paired it.
 *
 * With no connection none of that was reachable. The settings read fails, so the person's own pair is
 * unknown; the platform's default session cannot be hydrated; and the chain runs down to the in-app
 * fallback constants, whose ids (`orbiter-fallback`, `fallback-world`) are then REQUESTED from the
 * API — a request that fails, leaving the plain sphere. A downloaded library could play its audio and
 * not draw the world around it.
 *
 * So a download keeps three things: the pair's ids and versions (in the manifest), the two release
 * payloads (in the release store, keyed like any other release), and the world's texture files (in the
 * bucket, pinned like any other texture). This module puts all three back:
 *
 *   - the ids become the resolution chain's answer when live settings/platform reads cannot answer,
 *   - the releases go into the cache the assembler checks BEFORE it fetches, so it never fetches,
 *   - the texture bytes are answered by the worker, for the real URL, as for any other texture.
 *
 * Nothing here decides anything. It restores what was already resolved when there was a connection.
 */
import { primeDefaultPair } from '../config/defaultPair.js';
import { primeOrbiterReleaseCache, primeWorldReleaseCache } from '../api/dataManager/loaders.js';
import {
  RELEASE_KIND,
  listDownloadedCollections,
  readStoredRelease,
  releaseRecordKey,
} from './offlineLibraryStore.js';

function normalize(value) {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  return trimmed.length ? trimmed : null;
}

/**
 * The pair a download recorded, from whichever stored collection has one.
 *
 * Any of them will do while they name the same answer. If the person changed their default between
 * downloads, prefer when the pair itself was resolved — never `updatedAt`, which every repair,
 * progress patch and update changes without learning anything new about the pair.
 */
async function readStoredPair() {
  const stored = await listDownloadedCollections().catch(() => []);
  const withPair = stored.filter((manifest) => manifest?.defaultPair?.orbiterId && manifest?.defaultPair?.worldId);
  if (!withPair.length) return null;
  const resolvedAt = (manifest) => (
    Number(manifest.defaultPair?.resolvedAt) || Number(manifest.downloadedAt) || 0
  );
  withPair.sort((a, b) => resolvedAt(b) - resolvedAt(a));
  return withPair[0].defaultPair;
}

/**
 * Make the stored fallback pair available to everything that asks for one offline.
 *
 * Safe to call more than once and safe to call online — it only ever installs what a download already
 * resolved, and a session with a connection will have resolved the same thing itself. Never throws:
 * no stored pair means the behaviour that was there before this existed.
 *
 * @returns {Promise<{ orbiterId: string, worldId: string }|null>} the pair that was installed
 */
export async function primeStoredDefaultPair() {
  try {
    const pair = await readStoredPair();
    if (!pair) return null;

    const orbiterId = normalize(pair.orbiterId);
    const worldId = normalize(pair.worldId);
    if (!orbiterId || !worldId) return null;

    // The releases FIRST, because the pair is not installed without them. Everything that reads a
    // pair reads its releases inline — a file on this machine builds its whole synthetic release from
    // them — so priming ids alone produced an entry with no hydration and a request for its own
    // `local:…` id as a track. Read by id AND the version the manifest pinned, which is how the
    // loaders will ask: an unpinned request resolves `version: null` and misses a numbered entry.
    const orbiterKey = releaseRecordKey({
      kind: RELEASE_KIND.ORBITER,
      id: orbiterId,
      version: pair.orbiterVersion,
    });
    const worldKey = releaseRecordKey({ kind: RELEASE_KIND.WORLD, id: worldId, version: pair.worldVersion });
    const [orbiterPayload, worldPayload] = await Promise.all([
      orbiterKey ? readStoredRelease(orbiterKey).catch(() => null) : null,
      worldKey ? readStoredRelease(worldKey).catch(() => null) : null,
    ]);

    if (!orbiterPayload?.release || !worldPayload?.release) {
      // Nothing is installed. Leaving the chain exactly as it was is the honest answer: it is what
      // happened before any of this existed, and a half-installed pair is the bug described above.
      console.warn(
        '[offline] A fallback pair was stored but its releases were not; leaving the usual resolution alone.',
        { orbiter: Boolean(orbiterPayload?.release), world: Boolean(worldPayload?.release) },
      );
      return null;
    }

    if (!primeDefaultPair(pair, {
      orbiterRelease: orbiterPayload.release,
      worldRelease: worldPayload.release,
    })) {
      return null;
    }

    // Also into the cache the loaders check before they reach for the network, so anything that asks
    // for these by id — rather than through the pair — finds them too.
    primeOrbiterReleaseCache(orbiterId, orbiterPayload);
    primeWorldReleaseCache(worldId, worldPayload);
    return { orbiterId, worldId };
  } catch (error) {
    console.warn('[offline] Could not restore the fallback orbiter and world.', error);
    return null;
  }
}
